import { useApolloClient } from "@apollo/client";
import { assertNever, omitDeep } from "@msys/common";
import { assign } from "lodash-es";
import {
  DefineItemProductSearchFilterDefinitionsInput,
  ItemProductSearchFilterBrandFilter,
  ItemProductSearchFilterBrandLineFilter,
  ItemProductSearchFilterIdFilter,
  ItemProductSearchFilterPriceFilter,
  ItemProductSearchFilterPropertyFilter,
} from "../../../clients/graphqlTypes.js";
import { useProductSearchFiltersSection_DefineProductSearchFilterDefinitionsMutation } from "./useProductSearchFilterDefinitionMutations.generated.js";

export function useDefineItemProductSearchFilterDefinitionsMutation(
  refetchQueries?: string[]
) {
  const client = useApolloClient();
  const [defineItemProductSearchFilterDefinitions, query] =
    useProductSearchFiltersSection_DefineProductSearchFilterDefinitionsMutation(
      {
        client,
        refetchQueries,
      }
    );

  return [defineItemProductSearchFilterDefinitions, query] as const;
}

type AllowedProductSearchFilterDefinitionKey = keyof Omit<
  DefineItemProductSearchFilterDefinitionsInput,
  "projectId" | "docId" | "itemId"
>;
type ProductSearchFilterDefinitionValue =
  DefineItemProductSearchFilterDefinitionsInput[AllowedProductSearchFilterDefinitionKey];

export function useProductSearchFilterDefinitionMutations({
  projectId,
  docId,
  itemId,
  productTypeSearchFilter,
  supplierSearchFilterDefinition,
  brandSearchFilterDefinition,
  brandLineSearchFilterDefinition,
  listPriceSearchFilterDefinition,
  propertySearchFiltersDefinitions,
  refetchQueries,
}: {
  projectId: string | null;
  docId: string;
  itemId: string;
  productTypeSearchFilter: { id: string } | undefined | null;
  supplierSearchFilterDefinition:
    | ItemProductSearchFilterIdFilter
    | undefined
    | null;
  listPriceSearchFilterDefinition:
    | ItemProductSearchFilterPriceFilter
    | undefined
    | null;
  brandSearchFilterDefinition:
    | ItemProductSearchFilterBrandFilter
    | undefined
    | null;
  brandLineSearchFilterDefinition:
    | ItemProductSearchFilterBrandLineFilter
    | undefined
    | null;
  propertySearchFiltersDefinitions: ItemProductSearchFilterPropertyFilter[];
  refetchQueries?: string[];
}) {
  const [defineItemProductSearchFilterDefinitions] =
    useDefineItemProductSearchFilterDefinitionsMutation(refetchQueries);

  const handleSetProductSearchFilterDefinition = async (
    key: AllowedProductSearchFilterDefinitionKey,
    value: ProductSearchFilterDefinitionValue
  ) => {
    const existingDefinitions: Pick<
      DefineItemProductSearchFilterDefinitionsInput,
      AllowedProductSearchFilterDefinitionKey
    > = {
      itemProductSearchFilterProductTypeIdFilterDefinition:
        productTypeSearchFilter
          ? {
              idFilter: {
                operatorId: "eq",
                valueId: productTypeSearchFilter.id,
              },
            }
          : undefined,

      itemProductSearchFilterSupplierIdFilterDefinition:
        supplierSearchFilterDefinition
          ? supplierSearchFilterDefinition.__typename ===
            "EntitySearchIdInFilter"
            ? {
                idInFilter: omitDeep(
                  supplierSearchFilterDefinition,
                  "__typename"
                ),
              }
            : supplierSearchFilterDefinition.__typename ===
                "EntitySearchIdFilter"
              ? {
                  idFilter: omitDeep(
                    supplierSearchFilterDefinition,
                    "__typename"
                  ),
                }
              : assertNever(supplierSearchFilterDefinition)
          : undefined,
      itemProductSearchFilterBrandFilterDefinition: brandSearchFilterDefinition
        ? brandSearchFilterDefinition.__typename === "EntitySearchTextInFilter"
          ? {
              textInFilter: omitDeep(brandSearchFilterDefinition, "__typename"),
            }
          : brandSearchFilterDefinition.__typename === "EntitySearchTextFilter"
            ? {
                textFilter: omitDeep(brandSearchFilterDefinition, "__typename"),
              }
            : assertNever(brandSearchFilterDefinition)
        : undefined,
      itemProductSearchFilterBrandLineFilterDefinition:
        brandLineSearchFilterDefinition
          ? brandLineSearchFilterDefinition.__typename ===
            "EntitySearchTextInFilter"
            ? {
                textInFilter: omitDeep(
                  brandLineSearchFilterDefinition,
                  "__typename"
                ),
              }
            : brandLineSearchFilterDefinition.__typename ===
                "EntitySearchTextFilter"
              ? {
                  textFilter: omitDeep(
                    brandLineSearchFilterDefinition,
                    "__typename"
                  ),
                }
              : assertNever(brandLineSearchFilterDefinition)
          : undefined,
      itemProductSearchFilterListPriceFilterDefinition:
        listPriceSearchFilterDefinition
          ? listPriceSearchFilterDefinition.__typename ===
            "EntitySearchNumberBetweenFilter"
            ? {
                numberBetweenFilter: omitDeep(
                  listPriceSearchFilterDefinition,
                  "__typename"
                ),
              }
            : listPriceSearchFilterDefinition.__typename ===
                "EntitySearchNumberFilter"
              ? {
                  numberFilter: omitDeep(
                    listPriceSearchFilterDefinition,
                    "__typename"
                  ),
                }
              : assertNever(listPriceSearchFilterDefinition)
          : undefined,
      itemProductSearchFilterPropertyFilterDefinitions:
        propertySearchFiltersDefinitions?.map(
          ProductSearchFilterPropertyFilterDefinitionToInputValue
        ),
    };

    await defineItemProductSearchFilterDefinitions({
      variables: {
        input: {
          projectId,
          docId,
          itemId,
          ...assign(existingDefinitions, { [key]: value }),
        },
      },
    });
  };
  const handleDeleteProductSearchFilterDefinition = async (
    key: AllowedProductSearchFilterDefinitionKey
  ) => {
    return handleSetProductSearchFilterDefinition(key, null);
  };

  return {
    handleSetProductSearchFilterDefinition,
    handleDeleteProductSearchFilterDefinition,
    defineItemProductSearchFilterDefinitions,
  };
}

export function ProductSearchFilterPropertyFilterDefinitionToInputValue(
  filter: ItemProductSearchFilterPropertyFilter
) {
  switch (filter.__typename) {
    case "EntitySearchPropertyFilterBoolInFilter": {
      return {
        boolInFilter: omitDeep(filter, "__typename"),
      };
    }
    case "EntitySearchPropertyFilterBoolFilter": {
      return {
        boolFilter: omitDeep(filter, "__typename"),
      };
    }
    case "EntitySearchPropertyFilterNumberInFilter": {
      return {
        numberInFilter: omitDeep(filter, "__typename"),
      };
    }
    case "EntitySearchPropertyFilterNumberBetweenFilter": {
      return {
        numberBetweenFilter: omitDeep(filter, "__typename"),
      };
    }
    case "EntitySearchPropertyFilterNumberFilter": {
      return {
        numberFilter: omitDeep(filter, "__typename"),
      };
    }
    case "EntitySearchPropertyFilterTextInFilter": {
      return {
        textInFilter: omitDeep(filter, "__typename"),
      };
    }
    case "EntitySearchPropertyFilterTextFilter": {
      return {
        textFilter: omitDeep(filter, "__typename"),
      };
    }
  }
}
